import { useCallback, useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import WebhookHealthBadge from "./WebhookHealthBadge";
import { getWebhookErrorCategory } from "./utils/webhookErrorCategory";

const STATUS_OPTIONS = ["Pending", "Success", "Failed"];
const EVENT_TYPE_OPTIONS = [
  "webhook.test",
  "message.sent",
  "message.delivered",
  "message.read",
  "message.failed",
  "message.inbound.received",
  "provider.meta.raw",
];

const toData = response =>
  response?.data?.data ?? response?.data?.payload ?? response?.data ?? null;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const formatDate = value => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const trimSnippet = value => {
  const text = String(value || "").trim();
  if (!text) return "-";
  return text.length <= 120 ? text : `${text.slice(0, 117)}...`;
};

const needsAttention = status => status === "Failing" || status === "Degraded";

const getObservedPattern = rows => {
  const list = Array.isArray(rows) ? rows : [];
  const totalRows = list.length;
  const failedRows = list.filter(row => row?.status === "Failed");
  const pendingRows = list.filter(row => row?.status === "Pending");

  if (pendingRows.length >= 3) {
    return {
      kind: "retry_heavy",
      message: "Multiple retries are still pending for recent attempts.",
    };
  }

  if (failedRows.length >= 3) {
    const categoryCounts = failedRows.reduce((acc, row) => {
      const category = getWebhookErrorCategory({
        pendingRetryCount: row?.status === "Pending" ? 1 : 0,
        lastFailureStatusCode: row?.responseStatusCode,
        lastFailureSnippet: row?.responseBodySnippet,
      });

      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    const dominantEntry = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0];
    if (dominantEntry && dominantEntry[1] / failedRows.length >= 0.6) {
      return {
        kind: "dominant_category",
        category: dominantEntry[0],
        message: `Most recent failures are ${dominantEntry[0]}-related.`,
      };
    }
  }

  if (totalRows >= 5 && failedRows.length / totalRows >= 0.7) {
    return {
      kind: "high_failure_ratio",
      message: "Most recent attempts are failing.",
    };
  }

  return null;
};

const getOperatorRecommendation = ({
  healthStatus,
  category,
  pattern,
  pendingRetryCount,
}) => {
  if (!needsAttention(healthStatus)) {
    return "";
  }

  if (pattern?.kind === "retry_heavy") {
    return "Recent attempts are still retrying. Wait for retries to complete before manual replay.";
  }

  if (pattern?.kind === "dominant_category" && pattern.category) {
    return `Recent attempts show repeated ${pattern.category} issues. Focus on resolving this before retrying.`;
  }

  if (pattern?.kind === "high_failure_ratio") {
    return "Most recent attempts are failing. Investigate failures before triggering more retries.";
  }

  const resolvedCategory =
    category || (Number(pendingRetryCount ?? 0) > 0 ? "Retrying" : "");
  const recommendationByCategory = {
    Auth: "Verify webhook authentication or secret configuration.",
    NotFound: "Check webhook URL and route configuration.",
    ServerError: "Check destination server health and logs.",
    Connectivity: "Verify endpoint availability and network connectivity.",
    Retrying: "Wait for retry attempts to complete before taking action.",
  };

  return (
    recommendationByCategory[resolvedCategory] ||
    "Review recent failures and validate endpoint using a test callback."
  );
};

export default function WebhookLogsTable({
  endpoints,
  refreshTick,
  onRefreshComplete,
  onHealthChanged,
}) {
  const [endpointId, setEndpointId] = useState("");
  const [status, setStatus] = useState("");
  const [eventType, setEventType] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replayingId, setReplayingId] = useState("");
  const [page] = useState(1);
  const [pageSize] = useState(50);
  const hasActiveFilters = Boolean(status || eventType);

  useEffect(() => {
    if (!Array.isArray(endpoints) || endpoints.length === 0) {
      if (endpointId) {
        setEndpointId("");
      }
      return;
    }

    const hasSelectedEndpoint = endpoints.some(endpoint => endpoint.id === endpointId);
    if (!endpointId || !hasSelectedEndpoint) {
      setEndpointId(endpoints[0].id);
    }
  }, [endpointId, endpoints]);

  const load = useCallback(async (tick = refreshTick) => {
    if (!endpointId) {
      setRows([]);
      if (typeof onRefreshComplete === "function") {
        onRefreshComplete(tick);
      }
      return;
    }
    setLoading(true);
    try {
      const params = { endpointId, page, pageSize };
      if (status) params.status = status;
      if (eventType) params.eventType = eventType;

      const res = await axiosClient.get("/webhooks/deliveries", {
        params,
        __silentGenericToast: true,
      });
      const data = toData(res);
      const list = Array.isArray(data)
        ? data
        : data?.items || data?.logs || data?.rows || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (error) {
      if (error?.response?.status !== 429) {
        toast.error(getErrorMessage(error, "Failed to load webhook deliveries"), {
          toastId: "integrations-webhook-logs-load",
        });
      }
    } finally {
      setLoading(false);
      if (typeof onRefreshComplete === "function") {
        onRefreshComplete(tick);
      }
    }
  }, [endpointId, eventType, onRefreshComplete, page, pageSize, refreshTick, status]);

  useEffect(() => {
    load(refreshTick);
  }, [load, refreshTick]);

  const replayDelivery = async row => {
    setReplayingId(row.id);
    try {
      await axiosClient.post(`/webhooks/deliveries/${row.id}/replay`, null, {
        __silentGenericToast: true,
      });
      toast.success("Replay queued");
      if (typeof onHealthChanged === "function") {
        onHealthChanged();
      } else {
        await load(refreshTick);
      }
    } catch (error) {
      if (error?.response?.status !== 429) {
        toast.error(getErrorMessage(error, "Failed to replay webhook delivery"));
      }
    } finally {
      setReplayingId("");
    }
  };

  const selectedEndpoint = (endpoints || []).find(endpoint => endpoint.id === endpointId) || null;
  const errorCategory = selectedEndpoint ? getWebhookErrorCategory(selectedEndpoint) : "Unknown";
  const observedPattern = selectedEndpoint ? getObservedPattern(rows) : null;
  const operatorRecommendation = selectedEndpoint
    ? getOperatorRecommendation({
        healthStatus: selectedEndpoint.healthStatus,
        category: errorCategory,
        pattern: observedPattern,
        pendingRetryCount: selectedEndpoint.pendingRetryCount,
      })
    : "";

  const clearFilters = () => {
    setStatus("");
    setEventType("");
  };

  return (
    <section className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="text-lg font-semibold text-slate-800">Delivery Logs</h2>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={endpointId}
            onChange={e => setEndpointId(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-2 text-sm min-w-[280px]"
          >
            <option value="">Select endpoint</option>
            {(endpoints || []).map(endpoint => (
              <option key={endpoint.id} value={endpoint.id}>
                {endpoint.url}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-2 text-sm"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <select
            value={eventType}
            onChange={e => setEventType(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-2 text-sm min-w-[220px]"
          >
            <option value="">All event types</option>
            {EVENT_TYPE_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Clear filters
          </button>
        </div>
      </div>

      {selectedEndpoint ? (
        <div className="mb-4 rounded-md border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-slate-800">Endpoint health</p>
              <p className="text-xs text-slate-500">{selectedEndpoint.url}</p>
              {needsAttention(selectedEndpoint.healthStatus) ? (
                <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-rose-700">
                  <AlertTriangle size={12} />
                  This endpoint needs attention
                </p>
              ) : null}
            </div>
            <WebhookHealthBadge status={selectedEndpoint.healthStatus} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setStatus("Failed")}
              className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
            >
              Show failed logs
            </button>
            <button
              type="button"
              onClick={() => setStatus("Pending")}
              className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
            >
              Show pending retries
            </button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Last success
              </p>
              <p className="mt-1 text-sm text-slate-800">
                {formatDate(selectedEndpoint.lastSuccessAtUtc)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Last failure
              </p>
              <p className="mt-1 text-sm text-slate-800">
                {formatDate(selectedEndpoint.lastFailureAtUtc)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Pending retries
              </p>
              <p className="mt-1 text-sm text-slate-800">
                {selectedEndpoint.pendingRetryCount ?? 0}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Recent failures
              </p>
              <p className="mt-1 text-sm text-slate-800">
                {selectedEndpoint.recentFailureCount ?? 0}
              </p>
            </div>
          </div>

          <div className="mt-3 grid gap-3 xl:grid-cols-[180px_minmax(0,1fr)]">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Latest failure code
              </p>
              <p className="mt-1 text-sm text-slate-800">
                {selectedEndpoint.lastFailureStatusCode ?? "-"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Latest failure snippet
              </p>
              <p className="mt-1 text-sm text-slate-700 break-words">
                {trimSnippet(selectedEndpoint.lastFailureSnippet)}
              </p>
            </div>
          </div>
          {operatorRecommendation ? (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <span className="font-medium text-slate-700">Recommendation:</span>{" "}
              {operatorRecommendation}
              {needsAttention(selectedEndpoint.healthStatus) && observedPattern ? (
                <>
                  {" "}
                  <span className="font-medium text-slate-700">Observed:</span>{" "}
                  {observedPattern.message}
                </>
              ) : null}
              {needsAttention(selectedEndpoint.healthStatus) && errorCategory ? (
                <>
                  {" "}
                  <span className="font-medium text-slate-700">Category:</span>{" "}
                  {errorCategory}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {!endpointId ? (
        <p className="text-sm text-slate-500">Select a webhook endpoint.</p>
      ) : loading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          {hasActiveFilters
            ? "No delivery logs match the current filters."
            : "No delivery logs yet. Send a test callback from Webhook Endpoints or wait for real delivery events."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-600">
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Event Type</th>
                <th className="py-2 pr-3">Attempts</th>
                <th className="py-2 pr-3">Last Attempt</th>
                <th className="py-2 pr-3">Response Code</th>
                <th className="py-2 pr-3">Response Snippet</th>
                <th className="py-2 pr-3">Next Retry</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} className="border-b border-slate-100">
                  <td className="py-2 pr-3">{row.status || "-"}</td>
                  <td className="py-2 pr-3">{row.eventType || "-"}</td>
                  <td className="py-2 pr-3">{row.attemptCount ?? "-"}</td>
                  <td className="py-2 pr-3">{formatDate(row.lastAttemptAtUtc)}</td>
                  <td className="py-2 pr-3">{row.responseStatusCode ?? "-"}</td>
                  <td className="py-2 pr-3 max-w-[280px] break-words text-slate-600">
                    {trimSnippet(row.responseBodySnippet)}
                  </td>
                  <td className="py-2 pr-3">{formatDate(row.nextRetryAtUtc)}</td>
                  <td className="py-2 pr-3">
                    {row.status === "Failed" ? (
                      <button
                        type="button"
                        onClick={() => replayDelivery(row)}
                        disabled={replayingId === row.id}
                        className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-60"
                      >
                        {replayingId === row.id ? "Replaying..." : "Replay"}
                      </button>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
